import { Inject, inject, Injectable } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { filter, map } from 'rxjs/operators';
import { EventMessage, EventType, RedirectRequest } from '@azure/msal-browser';
import { BehaviorSubject, firstValueFrom, Observable, tap } from 'rxjs';
import { ActivatedRoute } from '@angular/router';

const loginHintKey = 'login_hint';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  initialized$ = new BehaviorSubject(false);
  authenticated$ = new BehaviorSubject(false);
  token$ = new BehaviorSubject('');

  private msalBroadcastService = inject(MsalBroadcastService);
  private authService = inject(MsalService);
  private route = inject(ActivatedRoute);

  constructor(@Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration) {
    this.init();
  }

  async init(): Promise<void> {
    this.route.queryParamMap
      .pipe(
        map(paramMap => paramMap.get(loginHintKey)),
        filter(loginHint => !!loginHint)
      )
      .subscribe(loginHint => {
        localStorage.setItem(loginHintKey, loginHint);
      });

    this.msalBroadcastService.msalSubject$
      .subscribe(async (msg: EventMessage) => {
        switch (msg.eventType) {
          case EventType.LOGIN_SUCCESS:
          case EventType.ACQUIRE_TOKEN_SUCCESS:
            this.token$.next(msg.payload['accessToken']);
            this.checkIfAuthenticated();
            break;
          case EventType.ACQUIRE_TOKEN_FAILURE:
          case EventType.LOGIN_FAILURE:
          case EventType.LOGOUT_FAILURE:
          case EventType.ACQUIRE_TOKEN_BY_CODE_FAILURE:
          case EventType.SSO_SILENT_FAILURE:
            this.authService.instance.clearCache();
            break;
          case EventType.INITIALIZE_END:
            this.initialized$.next(true);
          case EventType.SSO_SILENT_SUCCESS:
          case EventType.ACQUIRE_TOKEN_BY_CODE_SUCCESS:
          case EventType.LOGOUT_SUCCESS:
          case EventType.LOGOUT_END:
            this.checkIfAuthenticated();
            break;
          case EventType.ACCOUNT_ADDED:
          case EventType.ACCOUNT_REMOVED:
            const isAuthenticated = this.checkIfAuthenticated();
            if (!isAuthenticated) {
              window.location.pathname = '/';
            } else {
              this.setActiveAccount();
            }
            break;
        }
      });

    if (!this.isAuthenticated()) {
      await this.silentLogin();
    } else {
      // Check for account mismatch after initialization
      this.checkAndHandleAccountMismatch();
    }
  }

  private checkIfAuthenticated(): boolean {
    const allAccounts = this.authService.instance.getAllAccounts();
    this.authenticated$.next(allAccounts.length > 0);
    return this.authenticated$.value;
  }

  async silentLogin(): Promise<void> {
    await this.waitForInitialized();
    let loginHint = localStorage.getItem(loginHintKey);
    if (this.msalGuardConfig?.authRequest) {
      try {
        await firstValueFrom(this.authService.loginPopup({ ...this.msalGuardConfig.authRequest, prompt: 'none', loginHint } as RedirectRequest));
      } catch (e) {
        if (e.errorCode === 'popup_window_error') {
          console.warn('Please allow popups to enable single-sign-on and avoid logging in again.');
        } else {
          throw e;
        }
      }
      this.checkIfAuthenticated();
    }
  }

  async login(): Promise<void> {
    await this.waitForInitialized();
    if (this.msalGuardConfig?.authRequest) {
      this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest, prompt: 'select_account' } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  private async waitForInitialized() {
    await firstValueFrom(this.initialized$.pipe(filter(isInitialized => isInitialized)));
  }

  logout(): void {
    this.authService.logoutRedirect();
    this.authenticated$.next(false);
  }

  isAuthenticated$(): Observable<boolean> {
    return this.authenticated$.asObservable();
  }

  isAuthenticated(): boolean {
    const accounts = this.authService.instance.getAllAccounts();
    return accounts.length > 0;
  }

  async getToken(): Promise<string> {
    await this.waitForInitialized();
    return this.token$.value;
  }

  /**
   * Check if the login_hint from Launchpad matches the cached account.
   * If there's a mismatch, perform a silent SSO login with the correct account.
   */
  private checkAndHandleAccountMismatch(): void {
    const loginHint = localStorage.getItem(loginHintKey);

    // Only check if there's a login hint from Launchpad
    if (!loginHint) {
      return;
    }

    const cachedAccount = this.authService.instance.getActiveAccount();

    // If there's a cached account and it doesn't match the login hint, perform SSO silent login
    if (cachedAccount && cachedAccount.username.toLowerCase() !== loginHint.toLowerCase()) {

      // Clear the MSAL cache first
      this.authService.instance.clearCache();

      // Trigger silent login with the new account
      this.silentLogin()
        .then(() => {
          // Reload the page to trigger the authenticated flow with the new account
          window.location.reload();
        })
        .catch((error) => {
          // If silent login fails, redirect to login page
          this.login();
        });
    }
  }

  private setActiveAccount() {
    const activeAccount = this.authService.instance.getActiveAccount();
    let allAccounts = this.authService.instance.getAllAccounts();
    let loginHint = localStorage.getItem(loginHintKey);
    if (loginHint && (!activeAccount || activeAccount.username !== loginHint)) {
      let accountOfLoginHint = allAccounts.find(account => account.username === loginHint);
      if (accountOfLoginHint) {
        this.authService.instance.setActiveAccount(accountOfLoginHint);
        return;
      }
    }
    if (!activeAccount && allAccounts.length > 0) {
      this.authService.instance.setActiveAccount(allAccounts[0]);
    }
  }
}
